

import { Alert, Modal } from 'components';
import s from './styles.module.scss';
import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import * as api from 'api';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { formatPlanOptionLabel } from 'helpers/membershipFees';

// Throttle utility to batch API calls
const batchRequests = async (items, batchSize, fn, delay = 100) => {
  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(fn));
    results.push(...batchResults);
    if (i + batchSize < items.length) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  return results;
};

// const ITEMS_PER_PAGE = 50; // Number of rows to display per page

const StatusBadge = ({ value }) => (
  <span className={value ? s.badgeYes : s.badgeNo}>{value ? 'Yes' : 'No'}</span>
)

const computeUserMeta = (userData, hasBusiness) => {
  const planActive =
    userData?.plan &&
    userData.plan !== 'Plan 0' &&
    userData.planExpiresAt &&
    new Date(userData.planExpiresAt) > new Date()
  const isPaymentDone = !!planActive
  const isProfileComplete = isPaymentDone && !!hasBusiness
  return {
    isPaymentDone,
    isProfileComplete,
    isFullyOnboarded: isProfileComplete,
    hasBusiness: !!hasBusiness,
  }
}

const matchesStatusFilters = (meta, filters) => {
  if (filters.fullyOnboarded && meta.isFullyOnboarded !== (filters.fullyOnboarded === 'Yes')) return false
  if (filters.paymentDone && meta.isPaymentDone !== (filters.paymentDone === 'Yes')) return false
  if (filters.profileComplete && meta.isProfileComplete !== (filters.profileComplete === 'Yes')) return false
  if (filters.businessAdded && meta.hasBusiness !== (filters.businessAdded === 'Yes')) return false
  return true
}

const Main = forwardRef(function UsersTable({ userData, onExportingChange }, ref) {
  const [sortedData, setSortedData] = useState(userData);
  const [membershipFees, setMembershipFees] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    city: '',
    contactNo: '',
    fullyOnboarded: '',
    paymentDone: '',
    profileComplete: '',
    businessAdded: '',
    exempt: '',
    plan: '',
  });
  // const [currentPage, setCurrentPage] = useState(1);
  const [filteredData, setFilteredData] = useState(sortedData);
  const [sortOption, setSortOption] = useState('');
  const [filtering, setFiltering] = useState(false);
  const enrichCache = useRef(new Map());
  const filterDebounceRef = useRef(null);

  const navigate = useNavigate(); 
  const someRef = useRef();

 
  useEffect(() => {
    setSortedData(userData);
    setFilteredData(userData);
    enrichCache.current.clear();
  }, [userData]);

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.admin.settings.fetch({})
        if (!cancelled && res?.code === 200) {
          setMembershipFees(res.payload?.settings?.membershipFees || null)
        }
      } catch (e) {
        console.warn('Could not load membership fees for users table', e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, []);

  const normalizePhoneNumber = (phoneNo) => {
    if (!phoneNo) return '';
    return phoneNo.toString().replace(/\D/g, '');
  };

  const needsStatusEnrichment = Boolean(
    filterOptions.fullyOnboarded ||
    filterOptions.paymentDone ||
    filterOptions.profileComplete ||
    filterOptions.businessAdded
  );

  const enrichUser = useCallback(async (user) => {
    if (enrichCache.current.has(user._id)) {
      return enrichCache.current.get(user._id);
    }

    const userResponse = await api.admin.user.fetchSingle({ id: user._id });
    const fullUser = userResponse?.payload || {};
    const businessCheckResponse = await api.admin.business.checkUserBusiness({ userId: user._id });
    const hasBusiness = !!businessCheckResponse?.payload?.hasBusiness;
    const meta = {
      ...fullUser,
      ...computeUserMeta(fullUser, hasBusiness),
      businessSubCategories: hasBusiness ? businessCheckResponse.payload.subCategories || [] : [],
    };

    enrichCache.current.set(user._id, meta);
    return meta;
  }, []);

  const filterByContact = useCallback((users, contactFilter) => {
    if (!contactFilter) return users;
    const normalizedContactFilter = normalizePhoneNumber(contactFilter);
    return users.filter((user) => {
      const userContactNo = user.contactNo || user.phoneNo;
      if (!userContactNo) return false;
      return normalizePhoneNumber(userContactNo).includes(normalizedContactFilter);
    });
  }, []);

  const applyFilters = useCallback(async () => {
    const contactFilter = filterOptions.contactNo?.trim() || '';

    // No filters at all — show everything instantly
    if (!contactFilter && !needsStatusEnrichment) {
      setFiltering(false);
      setFilteredData(sortedData);
      return;
    }

    // Contact-only filter — instant, no API calls
    if (contactFilter && !needsStatusEnrichment) {
      setFiltering(false);
      setFilteredData(filterByContact(sortedData, contactFilter));
      return;
    }

    setFiltering(true);

    try {
      let candidates = filterByContact(sortedData, contactFilter);
      const matchedUsers = [];

      await batchRequests(
        candidates,
        8,
        async (user) => {
          try {
            const meta = await enrichUser(user);
            const phoneNoString = meta.phoneNo ? normalizePhoneNumber(meta.phoneNo) : '';
            const contactNoString = meta.contactNo ? normalizePhoneNumber(meta.contactNo) : '';
            const allPhoneNumbers = [phoneNoString, contactNoString].filter(Boolean).join(' ');
            const normalizedContactFilter = contactFilter ? normalizePhoneNumber(contactFilter) : '';

            const matchesContact =
              !normalizedContactFilter || allPhoneNumbers.includes(normalizedContactFilter);
            const matchesStatus = matchesStatusFilters(meta, filterOptions);

            if (matchesContact && matchesStatus) {
              matchedUsers.push(user);
            }
          } catch (error) {
            console.error('Error enriching user for filter:', user._id, error);
          }
        },
        50
      );

      setFilteredData(matchedUsers);
    } catch (error) {
      console.error('Error applying filters:', error);
      Alert.error('Failed to apply filters.');
    } finally {
      setFiltering(false);
    }
  }, [sortedData, filterOptions, needsStatusEnrichment, enrichUser, filterByContact]);

  // Debounce filter changes — avoids firing API calls on every keystroke
  useEffect(() => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    filterDebounceRef.current = setTimeout(() => {
      applyFilters();
    }, needsStatusEnrichment ? 400 : 200);

    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, [applyFilters, needsStatusEnrichment]);

  // const handleFilterChange = (column, value) => {
  //   setFilterOptions((prevOptions) => ({
  //     ...prevOptions,
  //     [column]: value,
  //   }));
  //   setCurrentPage(1); // Reset to the first page when filters are changed
  // };
  const handleFilterChange = (column, value) => {
    setFilterOptions((prevOptions) => ({
      ...prevOptions,
      [column]: value,
    }));
  };

  const clearFilters = () => {
    setFilterOptions({
      city: '',
      contactNo: '',
      fullyOnboarded: '',
      paymentDone: '',
      profileComplete: '',
      businessAdded: '',
      exempt: '',
      plan: '',
    });
  };

  const hasActiveFilters = Object.values(filterOptions).some(Boolean);

  const handleSort = (option) => {
    setSortOption(option);
    const sorted = [...filteredData];
    const parseDate = (date) => {
      if (!date) return new Date(0);
      const parsedDate = new Date(date);
      return isNaN(parsedDate) ? new Date(0) : parsedDate;
    };

    if (option === 'A-Z') {
      sorted.sort((a, b) => {
        const nameA = (a.firstName + ' ' + a.lastName || '').toLowerCase();
        const nameB = (b.firstName + ' ' + b.lastName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (option === 'Z-A') {
      sorted.sort((a, b) => {
        const nameA = (a.firstName + ' ' + a.lastName || '').toLowerCase();
        const nameB = (b.firstName + ' ' + b.lastName || '').toLowerCase();
        return nameB.localeCompare(nameA);
      });
    } else if (option === 'Joining Date Asc') {
      sorted.sort((a, b) => parseDate(a.createdAt) - parseDate(b.createdAt));
    } else if (option === 'Joining Date Desc') {
      sorted.sort((a, b) => parseDate(b.createdAt) - parseDate(a.createdAt));
    }

    setSortedData(sorted);
    setFilteredData(sorted);
    // setCurrentPage(1); // Reset to the first page after sorting
  };

  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    onExportingChange?.(exporting);
  }, [exporting, onExportingChange]);

  const handleExportToExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const fullUserData = await batchRequests(
        sortedData,
        8,
        async (user) => {
          try {
            const meta = await enrichUser(user);

            let isExempt = false;
            try {
              const approvalResult = await api.admin.user.fetchApprovalStatus({ id: user._id });
              if (approvalResult?.payload?.approvalStatus === 'Approved') {
                isExempt = true;
              }
            } catch (e) {
              console.error('Error fetching approval status for', user._id, e);
            }

            return {
              'First Name': meta.firstName || user.firstName || '',
              'Last Name': meta.lastName || user.lastName || '',
              'Contact Number': meta.phoneNo || user.phoneNo || '',
              'Email': meta.email || 'N/A',
              'City': meta.city || 'N/A',
              'Business Added': meta.hasBusiness ? 'Yes' : 'No',
              'Business Sub-category': meta.businessSubCategories?.length > 0 ? meta.businessSubCategories.join(', ') : 'N/A',
              'Joining Date': meta.createdAt ? new Date(meta.createdAt).toLocaleDateString() : 'N/A',
              'Payment Date': meta.paymentDate
                ? new Date(meta.paymentDate).toLocaleDateString()
                : meta.plan && meta.plan !== 'Plan 0' && meta.updatedAt
                  ? new Date(meta.updatedAt).toLocaleDateString()
                  : 'N/A',
              'Fully Onboarded': meta.isFullyOnboarded ? 'Yes' : 'No',
              'Profile Complete': meta.isProfileComplete ? 'Yes' : 'No',
              'Payment Done': meta.isPaymentDone ? 'Yes' : 'No',
              'Exempt': isExempt ? 'Yes' : 'No',
              'Plan': meta.plan || 'Default',
              'Plan Expiry Date': meta.planExpiresAt ? new Date(meta.planExpiresAt).toLocaleDateString() : 'N/A',
            };
          } catch (e) {
            console.error('Error processing user for export:', user._id, e);
            return {
              'First Name': user.firstName || '',
              'Last Name': user.lastName || '',
              'Contact Number': user.phoneNo || '',
              'Email': 'N/A',
              'City': 'N/A',
              'Business Added': 'N/A',
              'Business Sub-category': 'N/A',
              'Joining Date': user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A',
              'Payment Date': 'N/A',
              'Fully Onboarded': 'N/A',
              'Profile Complete': 'N/A',
              'Payment Done': 'N/A',
              'Exempt': 'N/A',
              'Plan': 'N/A',
              'Plan Expiry Date': 'N/A',
            };
          }
        },
        50
      );

      const worksheet = XLSX.utils.json_to_sheet(fullUserData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'User Data');
      XLSX.writeFile(workbook, 'user_data.xlsx');
      Alert.success('User data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      Alert.error('Failed to export user data.');
    } finally {
      setExporting(false);
    }
  };

  useImperativeHandle(ref, () => ({
    exportToExcel: handleExportToExcel,
    exporting,
  }));

  // const handlePageChange = (newPage) => {
  //   setCurrentPage(newPage);
  // };

  // const paginatedData = useMemo(() => {
  //   return filteredData.slice(
  //     (currentPage - 1) * ITEMS_PER_PAGE,
  //     currentPage * ITEMS_PER_PAGE
  //   );
  // }, [filteredData, currentPage]); 
  const paginatedData = filteredData;

  return (
    <div className={s.main}>
      <div className={s.tableToolbar}>
        <p className={s.summary}>
          Showing <strong>{filteredData.length}</strong> of <strong>{sortedData.length}</strong> users
          {filtering && <span className={s.filteringHint}> — filtering…</span>}
        </p>
        <div className={s.sortControl}>
          <label htmlFor="users-sort">Sort by</label>
          <select
            id="users-sort"
            value={sortOption}
            onChange={(e) => handleSort(e.target.value)}
          >
            <option value="">Default</option>
            <option value="A-Z">Name A–Z</option>
            <option value="Z-A">Name Z–A</option>
            <option value="Joining Date Asc">Oldest joined</option>
            <option value="Joining Date Desc">Newest joined</option>
          </select>
        </div>
      </div>

      <div className={s.filterBar}>
        <div className={s.filterItem}>
          <label htmlFor="filter-contact">Contact No</label>
          <input
            id="filter-contact"
            type="text"
            value={filterOptions.contactNo || ''}
            onChange={(e) => handleFilterChange('contactNo', e.target.value)}
            placeholder="Filter by number"
          />
        </div>
        <div className={s.filterItem}>
          <label htmlFor="filter-onboarded">Fully Onboarded</label>
          <select
            id="filter-onboarded"
            value={filterOptions.fullyOnboarded}
            onChange={(e) => handleFilterChange('fullyOnboarded', e.target.value)}
          >
            <option value="">All</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div className={s.filterItem}>
          <label htmlFor="filter-payment">Payment Done</label>
          <select
            id="filter-payment"
            value={filterOptions.paymentDone}
            onChange={(e) => handleFilterChange('paymentDone', e.target.value)}
          >
            <option value="">All</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div className={s.filterItem}>
          <label htmlFor="filter-profile">Profile Complete</label>
          <select
            id="filter-profile"
            value={filterOptions.profileComplete}
            onChange={(e) => handleFilterChange('profileComplete', e.target.value)}
          >
            <option value="">All</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div className={s.filterItem}>
          <label htmlFor="filter-business">Business Added</label>
          <select
            id="filter-business"
            value={filterOptions.businessAdded}
            onChange={(e) => handleFilterChange('businessAdded', e.target.value)}
          >
            <option value="">All</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        {hasActiveFilters && (
          <button type="button" className={s.clearFilters} onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <div className={s.tableWrapper}>
        <div className={s.table}>
          <div className={s.tableHeading}>
            <div className={s.srNo}>S.No.</div>
            <div className={s.userName}>User Name</div>
            <div className={s.city}>City</div>
            <div className={s.phoneNo}>Contact No</div>
            <div className={s.fullyOnboarded}>Fully Onboarded</div>
            <div className={s.paymentDone}>Payment Done</div>
            <div className={s.profileComplete}>Profile Complete</div>
            <div className={s.businessAdded}>Business Added</div>
            <div className={s.subCategories}>Business Sub-Category</div>
            <div className={s.exempt}>Exempt</div>
            <div className={s.joiningDate}>Joining Date</div>
            <div className={s.paymentDate}>Payment Date</div>
            <div className={s.plan}>Plan</div>
            <div className={s.planExpiryDate}>Plan Expiry</div>
            <div className={s.paymentButton}>Payment</div>
            <div className={s.actions}>Actions</div>
          </div>
          <div className={s.tableBody}>
            {paginatedData.map((user, i) => (
              <TableRow
                key={user._id}
                {...user}
                index={i}
                membershipFees={membershipFees}
                enrichCache={enrichCache}
                onUpdateUserData={(updatedUserData) => {
                  const updatedSortedData = [...sortedData];
                  updatedSortedData[i] = { ...updatedSortedData[i], ...updatedUserData };
                  setSortedData(updatedSortedData);
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Pagination */}
      {/* <div className={s.pagination}>
        {Array.from({ length: Math.ceil(sortedData.length / ITEMS_PER_PAGE) }, (_, i) => (
          <button
            key={i}
            className={currentPage === i + 1 ? s.active : ''}
            onClick={() => handlePageChange(i + 1)}
          >
            {i + 1}
          </button>
        ))}
      </div> */}
    </div>
  );
});

export default Main;



const TableRow = ({
  _id,
  firstName,
  lastName,
  isBlocked,
  city,
  contactNo,
  fullyOnboarded,
  paymentDone,
  profileComplete,
  businessAdded,
  exempt,
  paymentDate: propPaymentDate,
  index,
  membershipFees,
  enrichCache,
}) => {
  const [userBlockStatus, setUserBlockStatus] = useState(isBlocked);
  const [isExempt, setIsExempt] = useState(exempt);
  const [isPaymentDone, setIsPaymentDone] = useState(paymentDone);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userContactNo, setUserContactNo] = useState(contactNo);
  const [joiningDate, setJoiningDate] = useState(null);
  const [paymentDateState, setPaymentDateState] = useState(null);
  const [planExpiryDate, setPlanExpiryDate] = useState(null);
  const [usercity, setuserCity] = useState(null);
  const [hasBusiness, setHasBusiness] = useState(false);
  const [businessCategory, setBusinessCategory] = useState('No Business');
  const [businessSubCategories, setBusinessSubCategories] = useState([]);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [userFullName, setUserFullName] = useState(firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || '');
  const processing = useRef(false);
  const Navigate = useNavigate();

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (processing.current) return;
      processing.current = true;

      const cached = enrichCache?.current?.get(_id);
      if (cached) {
        setUserContactNo(cached.phoneNo);
        setSelectedPlan(cached.plan);
        setJoiningDate(cached.createdAt);
        setPaymentDateState(
          cached.paymentDate || (cached.plan && cached.plan !== 'Plan 0' ? cached.updatedAt : null)
        );
        setPlanExpiryDate(cached.planExpiresAt);
        setuserCity(cached.city);
        setUserFullName(`${cached.firstName || ''} ${cached.lastName || ''}`.trim() || cached.firstName);
        setHasBusiness(cached.hasBusiness);
        setBusinessSubCategories(cached.businessSubCategories || []);
        processing.current = false;
        return;
      }

      await new Promise(resolve => setTimeout(resolve, index * 50));

      try {
        const response = await api.admin.user.fetchSingle({ id: _id });

        if (response?.payload) {
          const user = response.payload;
          if (user) {
            const { phoneNo, plan, createdAt, paymentDate, updatedAt, city, firstName, lastName, planExpiresAt } = user;
            setUserContactNo(phoneNo);
            setSelectedPlan(plan);
            setJoiningDate(createdAt);
            setPaymentDateState(
              paymentDate || (plan && plan !== 'Plan 0' ? updatedAt : null)
            );
            setPlanExpiryDate(planExpiresAt);
            setuserCity(city);
            setUserFullName(`${firstName || ''} ${lastName || ''}`.trim() || firstName);
          }
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        processing.current = false;
      }
    };

    fetchUserDetails();
  }, [_id, index, enrichCache]);

  useEffect(() => {
    const checkUserBusinessStatus = async (userId) => {
      const cached = enrichCache?.current?.get(userId);
      if (cached) return;

      await new Promise(resolve => setTimeout(resolve, index * 50 + 100));

      try {
        const result = await api.admin.business.checkUserBusiness({ userId });
        const hasBusiness = result?.payload?.hasBusiness;
        const businessCategory = result?.payload?.category || 'No Business';
        const businessSubCategories = result?.payload?.subCategories || [];

        if (hasBusiness) {
          setHasBusiness(true);
          setBusinessCategory(businessCategory);
          setBusinessSubCategories(businessSubCategories);
        } else {
          setHasBusiness(false);
          setBusinessCategory('No Business');
          setBusinessSubCategories([]);
        }
      } catch (error) {
        console.error('Error checking user business:', error);
        setHasBusiness(false);
        setBusinessCategory('No Business');
        setBusinessSubCategories([]);
      }
    };

    if (_id) checkUserBusinessStatus(_id);
  }, [_id, index, enrichCache]);

  useEffect(() => {
    const planIsActive = selectedPlan && selectedPlan !== 'Plan 0' && planExpiryDate && new Date(planExpiryDate) > new Date();
    setIsPaymentDone(!!planIsActive);
  }, [selectedPlan, planExpiryDate]);

  const createPaymentLink = async (sendSms) => {
    if (!_id || !selectedPlan || selectedPlan === 'Plan 0') {
      Alert.error('Please select a plan (A, B, or C) before creating payment.');
      return null;
    }

    const response = await api.admin.payment.sendPaymentLink({
      id: _id,
      plan: selectedPlan,
      sendSms,
    });

    if (response.code === 200 && response.payload?.paymentLink) {
      return response.payload;
    }

    Alert.error(response.message || 'Failed to create payment link.');
    return null;
  };

  const handleSendLink = async () => {
    if (paymentBusy) return;
    setPaymentBusy(true);
    try {
      const payload = await createPaymentLink(true);
      if (payload) {
        const rupees = payload.billing?.amountRupees;
        const amtLine = rupees != null ? ` Amount: ₹${rupees}.` : '';
        Alert.success(
          `Payment link sent by SMS to the user.${amtLine}`
        );
      }
    } catch (error) {
      console.error('Error sending payment link:', error);
      Alert.error(error.message || 'Failed to send payment link.');
    } finally {
      setPaymentBusy(false);
    }
  };

  const handleShowQr = async () => {
    if (paymentBusy) return;
    setPaymentBusy(true);
    try {
      const payload = await createPaymentLink(false);
      if (payload?.paymentLink?.short_url) {
        Modal.PaymentQr({
          userName: userFullName,
          phoneNo: userContactNo,
          plan: selectedPlan,
          amountRupees: payload.billing?.amountRupees,
          paymentUrl: payload.paymentLink.short_url,
        });
      }
    } catch (error) {
      console.error('Error creating payment QR:', error);
      Alert.error(error.message || 'Failed to create payment QR.');
    } finally {
      setPaymentBusy(false);
    }
  };

  const handleRemovePlan = async () => {
    if (paymentBusy || !_id) return;
    setPaymentBusy(true);
    try {
      const response = await api.admin.payment.removePaymentPlan({ id: _id });
      if (response.code === 200) {
        const now = new Date();
        setSelectedPlan('Plan 0');
        setPlanExpiryDate(now);
        setPaymentDateState(null);
        setIsPaymentDone(false);

        if (enrichCache?.current?.has(_id)) {
          const cached = enrichCache.current.get(_id);
          enrichCache.current.set(_id, {
            ...cached,
            plan: 'Plan 0',
            planExpiresAt: now,
            paymentDate: null,
          });
        }

        Alert.success(response.message || 'Plan removed successfully.');
      } else {
        Alert.error(response.message || 'Failed to remove plan.');
      }
    } catch (error) {
      console.error('Error removing plan:', error);
      Alert.error(error.message || 'Failed to remove plan.');
    } finally {
      setPaymentBusy(false);
    }
  };

  const confirmRemovePlan = () => {
    Modal.Confirm(
      `Remove ${selectedPlan} for ${userFullName || firstName}? This will reset them to the default plan.`,
      handleRemovePlan
    );
  };
  useEffect(() => {
    const fetchApprovalStatus = async () => {
      // Stagger requests by row index to avoid overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, index * 50 + 200));
      
      try {
        const result = await api.admin.user.fetchApprovalStatus({ id: _id });

        if (result?.payload?.approvalStatus === 'Approved') {
          setIsExempt(true);
        } else {
          setIsExempt(false);
        }
      } catch (error) {
        console.error('Error fetching approval status:', error);
        setIsExempt(false);
      }
    };

    if (_id) {
      fetchApprovalStatus();
    }
  }, [_id, index]);

  const BlockHandler = async () => {
    if (processing.current) return;
    processing.current = true;

    const toggleUserBlockStatus = await api.admin.user.blockUser({ id: _id });

    if (toggleUserBlockStatus.code === 201) {
      setUserBlockStatus(toggleUserBlockStatus.payload.status);
      toggleUserBlockStatus.payload.status
        ? Alert.warn(toggleUserBlockStatus.message)
        : Alert.success(toggleUserBlockStatus.message);
    } else {
      Alert.error(toggleUserBlockStatus.message);
    }
    processing.current = false;
  };

  const userHandler = async () => {
    if (processing.current) return;
    processing.current = true;

    const userAuth = await api.auth.admin.getUserAdmin({ id: _id });
    if (userAuth.code === 201) {
      // Preserve admin session if not already impersonating
      const alreadyImpersonating = window.localStorage.getItem('impersonating') === 'true';
      if (!alreadyImpersonating) {
        const currentAuth = window.localStorage.getItem('authorization');
        const currentUserData = window.localStorage.getItem('userData');
        if (currentAuth && currentUserData) {
          window.localStorage.setItem('adminAuthorization', currentAuth);
          window.localStorage.setItem('adminUserData', currentUserData);
        }
        window.localStorage.setItem('impersonating', 'true');
      }

      window.localStorage.setItem('authorization', userAuth.payload.authorization);
      window.localStorage.setItem('userData', JSON.stringify(userAuth.payload.userData));
      Navigate('/userAdmin/editProfile', { replace: true });
    } else {
      Alert.warn(userAuth.error);
    }
    processing.current = false;
  };  

  const handleExemptChange = () => {
    setIsExempt(!isExempt);
    setIsPaymentDone(!isExempt); 
  };

  const formattedJoiningDate = joiningDate ? new Date(joiningDate).toLocaleDateString() : 'N/A';
  const formattedPaymentDate = paymentDateState ? new Date(paymentDateState).toLocaleDateString() : 'N/A';
  const formattedPlanExpiryDate = planExpiryDate ? new Date(planExpiryDate).toLocaleDateString() : 'N/A';
  const isProfileComplete = isPaymentDone && hasBusiness;
  const isFullyOnboarded = isProfileComplete;
  return (
    <div className={s.tableRow}>
      <div className={s.srNo}>{index + 1}</div>
      <div className={s.userName} onClick={userHandler}>
        {userFullName || (firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'N/A')}
      </div>
      <div className={s.city}>{usercity || 'No City Available'}</div>
      <div className={s.phoneNo}>{userContactNo || 'No Contact Number Available'}</div>
      <div className={s.fullyOnboarded}><StatusBadge value={isFullyOnboarded} /></div>
      <div className={s.paymentDone}><StatusBadge value={isPaymentDone} /></div>
      <div className={s.profileComplete}><StatusBadge value={isProfileComplete} /></div>
      <div className={s.businessAdded}><StatusBadge value={hasBusiness} /></div>
      <div className={s.businessSubCategories} title={Array.isArray(businessSubCategories) ? businessSubCategories.join(', ') : businessSubCategories || 'N/A'}>
        {businessSubCategories && businessSubCategories.length > 0 
          ? (Array.isArray(businessSubCategories) 
              ? (businessSubCategories.length > 2 
                  ? `${businessSubCategories.slice(0, 2).join(', ')} +${businessSubCategories.length - 2} more`
                  : businessSubCategories.join(', '))
              : businessSubCategories)
          : 'N/A'}
      </div>
      <div className={s.exempt}>
        <input
          type="checkbox"
          checked={isExempt}
          onChange={handleExemptChange}
        />
      </div>
      <div className={s.joiningDate}>{formattedJoiningDate}</div>
      <div className={s.paymentDate}>{formattedPaymentDate}</div>
      <div className={s.plan}>
  {selectedPlan && selectedPlan !== 'Plan 0' ? (
    <div className={s.planActive}>
      <p>{selectedPlan}</p>
      <button
        type="button"
        className={s.removePlanBtn}
        onClick={confirmRemovePlan}
        disabled={paymentBusy}
        title="Remove plan (reset to default)"
      >
        {paymentBusy ? '…' : 'Remove'}
      </button>
    </div>
  ) : (
    <select
      value={selectedPlan || "Plan 0"}
      onChange={(e) => setSelectedPlan(e.target.value)}
    >
      <option value="Plan 0">Default</option>
      <option value="Plan A">{formatPlanOptionLabel('1 year', membershipFees, 'A')}</option>
      <option value="Plan B">{formatPlanOptionLabel('5 year', membershipFees, 'B')}</option>
      <option value="Plan C">{formatPlanOptionLabel('10 year', membershipFees, 'C')}</option>
    </select>
  )}
</div>
      <div className={s.planExpiryDate}>{formattedPlanExpiryDate}</div>
<div className={s.paymentButton}>
  {!isExempt && !isPaymentDone && (
    <div className={s.paymentActions}>
      <button
        type="button"
        className={s.sendLinkBtn}
        onClick={handleSendLink}
        disabled={paymentBusy}
        title="Send payment link by SMS"
      >
        {paymentBusy ? '…' : 'Link'}
      </button>
      <button
        type="button"
        className={s.qrBtn}
        onClick={handleShowQr}
        disabled={paymentBusy}
        title="Show QR code to share with user"
      >
        QR
      </button>
    </div>
  )}
</div>

      <div className={s.actions}>
        <button 
          onClick={() => 
            Modal.Confirm(
              `Are you sure you want to ${userBlockStatus ? 'unblock' : 'block'} ${firstName}?`,
              BlockHandler
            )
          }
          className={s.blockButton}
        >
          {userBlockStatus ? 'Unblock' : 'Block'}
        </button>
      </div>
    </div>
  );
};

